/**
 * Prompt fragments for the flow connector.
 *
 * Pure functions that translate a {@link TurnStimulus} into the strings the
 * runner's flow orchestrator splices into the per-turn agent prompt.
 * Extracted from `runner/src/flow-orchestrator.ts` in Phase 3 of the
 * flow-connector extraction (2026-05-08) so the connector owns its
 * prompt-rendering surface without a runtime dep on the orchestrator.
 *
 * The contract is byte-identical to the previous inline implementation —
 * any divergence is a regression.
 *
 * @docLink packages/factory-assets/flow#prompt-fragments
 */

import type { FlowExecution } from "@skaile/workspaces/types";
import type { FlowDefinition } from "./engine/types.js";
import {
  flowDefaults,
  isAgentNode,
  isCheckNode,
  isGateNode,
  nodeIsOptional,
  nodeLabel,
  nodeRequiresApproval,
} from "./engine/node-shape.js";

/**
 * Reasons why a new turn is starting. The flow orchestrator's prompt template
 * renders a stimulus-specific paragraph (via {@link renderStimulusPrompt}) so
 * the agent knows exactly why it has been invoked.
 *
 * @docLink packages/factory-assets/flow#turn-stimulus
 */
export type TurnStimulus =
  | { kind: "flow_started" }
  | {
      kind: "approval_received";
      nodeId: string;
      decision: "approved" | "rejected";
      feedback?: string;
      decidedBy: string;
    }
  | { kind: "input_received"; nodeId: string; response: unknown; providedBy: string }
  | { kind: "retry_requested"; nodeId: string; requestedBy: string }
  | { kind: "user_message"; text: string; senderId: string }
  | { kind: "resumed_after_hibernation" }
  | { kind: "cancelled" }
  | { kind: "state_changed" };

/**
 * Render the stimulus paragraph the agent sees at turn start.
 *
 * The output is a single paragraph (one or more sentences, no surrounding
 * whitespace) describing why the runner has woken the agent. Spliced into
 * the orchestrator prompt under the `## Why this turn is happening` heading.
 *
 * @docLink packages/factory-assets/flow#render-stimulus-prompt
 */
export function renderStimulusPrompt(stimulus: TurnStimulus): string {
  switch (stimulus.kind) {
    case "flow_started":
      return "The user just started this flow. Begin with the first available node — call `get_available`, then `build_handoff` for your chosen node, then `start_node`.";
    case "approval_received": {
      const { nodeId, decision, decidedBy, feedback } = stimulus;
      if (decision === "approved") {
        return `Node \`${nodeId}\` was **approved** by ${decidedBy}. Call \`complete_node\` for this node with the approved output, then advance to the next available node.`;
      }
      const fb = feedback ? ` Feedback: "${feedback}".` : "";
      return `Node \`${nodeId}\` was **rejected** by ${decidedBy}.${fb} Decide how to proceed: retry the node (the engine returned it to \`available\`), ask clarifying questions via \`request_input\`, or call \`fail_node(..., recoverable: false)\` if the issue cannot be addressed.`;
    }
    case "input_received":
      return `The user just provided input for node \`${stimulus.nodeId}\` (provided by ${stimulus.providedBy}). The node has returned to its prior status. Continue executing the node with this input.`;
    case "retry_requested":
      return `A user requested a retry of node \`${stimulus.nodeId}\` (requested by ${stimulus.requestedBy}). The node has been returned to \`available\` with its prior error preserved in history. Start a fresh attempt — review the error context if relevant and try a different approach.`;
    case "user_message":
      return `A user sent a message: "${stimulus.text}" (from ${stimulus.senderId}). You can respond directly without touching the flow, continue flow work, or both. If the message is a question about the flow, answer it; if it signals a new direction, consider whether to pause.`;
    case "resumed_after_hibernation":
      return "This session was hibernated and has just resumed. Review the current state above and continue where you left off. No state was lost.";
    case "cancelled":
      return "The user has cancelled this flow. Wrap up cleanly: do not start any new nodes. You may record a short summary if relevant, then stop.";
    case "state_changed":
      return "The flow state changed. Review the current state above and continue work accordingly.";
  }
}

/**
 * Build the full agent-orchestrator prompt for a flow turn.
 *
 * Reassembles the five-section prompt from the current FlowExecution snapshot
 * and the supplied stimulus:
 *
 *  1. Flow definition (markdown table of nodes)
 *  2. Current state (progress, available/blocked, interaction pointers)
 *  3. Why this turn is happening (stimulus-specific paragraph)
 *  4. Flow tools available (static documentation)
 *  5. Rules
 *
 * Originally a byte-identical lift of the (since-deleted)
 * `FlowOrchestrator.buildOrchestratorPrompt`; the contract now is simply
 * this function's own output. When strict v2 defaults or legacy globals are
 * non-empty, a `## Flow globals` section exposes those run-scoped values. The
 * flow-wide active-node table stays bounded to selection metadata; `build_handoff`
 * supplies a selected agent node's execution payload. First-class gates appear
 * as runtime-owned state but never as agent-executable work.
 * Pure — depends only on its inputs.
 *
 * @docLink packages/factory-assets/flow#build-orchestrator-prompt
 * @since 2.2.0
 */
export function buildOrchestratorPrompt(
  flowDef: FlowDefinition,
  state: FlowExecution,
  stimulus: TurnStimulus,
): string {
  const agentNodes = flowDef.nodes.filter(isAgentNode);
  const gateNodes = flowDef.nodes.filter(isGateNode);
  const checkNodes = flowDef.nodes.filter(isCheckNode);
  const promptNodes = flowDef.nodes.filter((node) => isAgentNode(node) || isGateNode(node));
  const agentNodeIds = new Set(agentNodes.map((node) => node.id));
  const promptNodeIds = new Set(promptNodes.map((node) => node.id));

  // ── Section 1: definition table ──
  const nodeRows = promptNodes
    .map((n) => {
      const deps =
        flowDef.edges
          .filter((e) => e.target === n.id && e.type === "flow")
          .map((e) => e.source)
          .join(", ") || "—";
      const required = nodeIsOptional(n) ? "optional" : "required";
      const gate = isGateNode(n) ? "first-class" : nodeRequiresApproval(n) ? "mandatory" : "—";
      const nodeState = state.nodes[n.id];
      const status = nodeState?.status ?? "not_started";
      const description = (n.description ?? n.data?.description ?? "—")
        .replace(/\s+/g, " ")
        .replaceAll("|", "\\|");
      return `| ${n.id} | ${nodeLabel(n)} | ${description} | ${required} | ${gate} | ${status} | ${deps} |`;
    })
    .join("\n");

  // ── Section 2: current state summary ──
  const nodesByStatus = (s: string) =>
    Object.values(state.nodes)
      .filter((n) => promptNodeIds.has(n.id) && n.status === s)
      .map((n) => n.id);
  const available = nodesByStatus("available");
  const awaitingApproval = nodesByStatus("awaiting_approval");
  const awaitingInput = nodesByStatus("awaiting_input");
  const running = nodesByStatus("running");
  const failed = nodesByStatus("failed");
  const completed = nodesByStatus("complete");
  const totalRequired = promptNodes.filter((n) => !nodeIsOptional(n)).length;

  const stateSummary = [
    `- Run ID: ${state.runId}`,
    `- Status: ${state.status}`,
    `- Autonomous mode: ${state.autonomousMode}`,
    `- Progress: ${completed.length} / ${totalRequired} required nodes complete`,
    `- Available now: ${available.join(", ") || "(none)"}`,
    `- Currently running: ${running.join(", ") || "(none)"}`,
    `- Awaiting approval: ${awaitingApproval.join(", ") || "(none)"}`,
    `- Awaiting input: ${awaitingInput.join(", ") || "(none)"}`,
    `- Failed: ${failed.join(", ") || "(none)"}`,
  ].join("\n");

  const outputCorrections = Object.values(state.nodes)
    .filter(
      (node) =>
        agentNodeIds.has(node.id) &&
        node.outputCorrection &&
        node.status !== "complete" &&
        node.status !== "failed",
    )
    .map(
      (node) =>
        `- Node \`${node.id}\`: ${node.outputCorrection?.issues.join("; ") ?? "unknown issue"}`,
    );
  const correctionSection =
    outputCorrections.length > 0
      ? `## Output correction required

The following node has already used its single corrective reprompt. Submit one corrected \`complete_node\` call that fixes every issue; a second mismatch fails the node.

${outputCorrections.join("\n")}

`
      : "";

  const rejectedGates = gateNodes.flatMap((node) => {
    const approval = state.nodes[node.id]?.approval;
    if (approval?.decision !== "rejected") return [];
    return [
      `- \`${node.id}\` — rejected by ${approval.decidedBy ?? "unknown reviewer"}: ${
        approval.feedback ?? "(no feedback provided)"
      }`,
    ];
  });
  const rejectedGateSection =
    rejectedGates.length > 0
      ? `## Rejected first-class gates requiring revision

${rejectedGates.join("\n")}

Revise the surrounding work in conversation, then re-request each gate with \`request_approval(nodeId, summary, artifacts)\`. Every gate remains parked until its new human decision; do not start, complete, skip, or fail a first-class gate.

`
      : "";

  // An override survives only in the approval record: the state table shows the
  // node as `complete` like any other, so without this section the failure is
  // invisible from the turn after the decision onward.
  const overriddenChecks = checkNodes.flatMap((node) => {
    const record = state.nodes[node.id];
    if (record?.status !== "complete" || record.approval?.decision !== "approved") return [];
    return [
      `- \`${node.id}\` — failed, overridden by ${record.approval.decidedBy ?? "unknown reviewer"}: ${
        record.approval.feedback ?? "(no reason recorded)"
      }`,
    ];
  });
  const overriddenCheckSection =
    overriddenChecks.length > 0
      ? `## Checks completed by human override

${overriddenChecks.join("\n")}

These checks did **not** pass. A human decided the run may continue anyway, so they are \`complete\` and their dependents are unblocked. Their evidence still records the failure — do not report them as passing, and say so if downstream work depends on what they were meant to verify.

`
      : "";

  // ── Section 2b: flow globals (additive — omitted entirely when empty so
  // flows without globals keep the byte-identical Phase 3 prompt) ──
  const globals = flowDefaults(flowDef);
  const globalsSection =
    Object.keys(globals).length > 0
      ? `## Flow globals

Run-scoped parameters set by the flow author or by the host that started this run. When \`run_input\` is present it carries the payload this run was started with (e.g. a scheduled or webhook-triggered run) — treat it as the run's initial input instead of asking the user for it.

\`\`\`json
${JSON.stringify(globals, null, 2)}
\`\`\`

`
      : "";

  // ── Section 3: stimulus paragraph ──
  const stimulusNode =
    stimulus.kind === "approval_received"
      ? flowDef.nodes.find((node) => node.id === stimulus.nodeId)
      : undefined;
  let stimulusParagraph = renderStimulusPrompt(stimulus);
  if (stimulus.kind === "approval_received" && stimulusNode && isGateNode(stimulusNode)) {
    if (stimulus.decision === "approved") {
      const availableSuccessors = agentNodes
        .filter((node) => state.nodes[node.id]?.status === "available")
        .map((node) => `\`${node.id}\``);
      stimulusParagraph = `Gate node \`${stimulus.nodeId}\` is already \`complete\` after approval by ${stimulus.decidedBy}. Do not call \`complete_node\` for the gate. Advance to successor work${
        availableSuccessors.length > 0
          ? `; available agent nodes: ${availableSuccessors.join(", ")}`
          : ""
      } — call \`get_available\`, then \`build_handoff\` and \`start_node\` for the chosen agent node.`;
    } else {
      stimulusParagraph = `Gate node \`${stimulus.nodeId}\` was **rejected** by ${stimulus.decidedBy}.${
        stimulus.feedback ? ` Feedback: "${stimulus.feedback}".` : ""
      } Revise the work in conversation and follow the rejected-gate instructions above.`;
    }
  }
  // A check is host-decided the same way a gate is: the adapter already wrote the
  // terminal status, and `complete_node` rejects any non-agent node — so the
  // generic approved/rejected wording would send the agent into a guaranteed throw.
  if (stimulus.kind === "approval_received" && stimulusNode && isCheckNode(stimulusNode)) {
    const availableSuccessors = agentNodes
      .filter((node) => state.nodes[node.id]?.status === "available")
      .map((node) => `\`${node.id}\``);
    stimulusParagraph =
      stimulus.decision === "approved"
        ? `Check node \`${stimulus.nodeId}\` failed, and ${stimulus.decidedBy} **overrode** the failure${
            stimulus.feedback ? `: "${stimulus.feedback}"` : ""
          }. The node is already \`complete\` and its evidence still records the failure. Do not call \`complete_node\` for it. Advance to successor work${
            availableSuccessors.length > 0
              ? `; available agent nodes: ${availableSuccessors.join(", ")}`
              : ""
          } — call \`get_available\`, then \`build_handoff\` and \`start_node\` for the chosen agent node.`
        : `Check node \`${stimulus.nodeId}\` failed and ${stimulus.decidedBy} **rejected** it${
            stimulus.feedback ? `: "${stimulus.feedback}"` : ""
          }. The node is already terminally \`failed\` — it did not return to \`available\`. Do not call \`complete_node\` or \`fail_node\` for it. Report the outcome and stop unless other nodes remain available.`;
  }

  // ── Section 4+5: static tool surface and rules ──
  return `# Flow Execution Context

You are orchestrating the flow **${flowDef.name}** (${flowDef.id} v${flowDef.version}). This is a governed flow execution: your job is to run nodes through the flow tools, summarize their outputs for human review, and yield control at approval gates.

## Flow definition

| ID | Label | Description | Required | Gate | Status | Depends on |
|----|-------|-------------|----------|------|--------|------------|
${nodeRows}

Nodes with **Gate = mandatory** are hard approval gates: they always require \`request_approval\` and a human decision, even when autonomous mode is on. They cannot be skipped — \`request_approval\` → approval is their only completion path.
Nodes with **Gate = first-class** are runtime-owned human decisions. The runtime parks them automatically and completes them directly on approval; the agent never executes them.

## Current state

${stateSummary}

${globalsSection}${correctionSection}${rejectedGateSection}${overriddenCheckSection}## Why this turn is happening

${stimulusParagraph}

## Flow tools available

All tools are exposed by the \`flow\` connector:

- \`get_available()\` — list node IDs that are ready to start
- \`get_state()\` — full current state including approvals, inputs, outputs
- \`get_globals()\` — the flow's run-scoped globals (e.g. the \`run_input\` payload for scheduled/params-bound runs)
- \`build_handoff(nodeId)\` — selected node instruction, supplied bindings, required assets, and upstream context
- \`start_node(nodeId)\` — transition an agent node from \`available\` → \`running\`; call before executing it. Never start a first-class gate.
- \`request_approval(nodeId, summary, artifacts)\` — transition a running agent node to \`awaiting_approval\`, or replace a rejected first-class gate's decision with a fresh pending request. **This ends your turn.**
- \`request_input(nodeId, prompt, schema)\` — transition to \`awaiting_input\`. **This ends your turn.** Legal from \`available\`, \`running\`, or \`awaiting_approval\`.
- \`complete_node(nodeId, summary, fields?, artifacts?, concerns?)\` — complete an agent node. When downstream nodes consume declared output fields, include a conforming JSON object. The first mismatch gets one corrective call; the second fails the node. Valid only if the agent node was approved, OR autonomousMode is true and it does not require mandatory approval. First-class gates are completed by the runtime on approval; never call this tool for them.
- \`fail_node(nodeId, message, recoverable)\` — transition to \`failed\`, or to \`skipped\` if the node is optional. \`recoverable=true\` returns the node to \`available\` instead.
- \`skip_node(nodeId)\` — transition to \`skipped\`. Only valid for optional nodes.

## Rules

1. Before executing an agent node, call \`build_handoff(nodeId)\` and follow its instruction with the supplied bindings, assets, and upstream context; then call \`start_node(nodeId)\`.
2. After executing the node, call \`request_approval\` unless \`autonomousMode\` is true — in which case call \`complete_node\` directly. Exception: nodes marked **Gate = mandatory** always require \`request_approval\` and a human decision, even in autonomous mode.
3. First-class nodes marked **Gate = first-class** are runtime-owned. Never start, complete, skip, fail, or request input for them. After rejection, revise in conversation and only call \`request_approval\` to create the next attempt.
4. If you need information from the user before or during agent execution, call \`request_input\`. This ends your turn until the user responds.
5. On unrecoverable agent failure, call \`fail_node(..., recoverable: false)\`; an optional node ends \`skipped\` rather than \`failed\`, so the run continues past it. On recoverable failure, call \`fail_node(..., recoverable: true)\` and the engine returns the node to \`available\`.
6. When all required nodes are \`complete\` or \`skipped\`, stop. The runner marks the flow done automatically.
7. Free-chat user messages do not require flow action. Respond directly, continue flow work, or both — your call.
`;
}

/**
 * Render the full prompt sent to a subprompt node's isolated driver instance.
 *
 * `instruction` is already interpolated with the node's resolved bindings
 * (the same `interpolateNodeInstruction` step an agent handoff uses) — this
 * function only adds the typed-response directive when the node declares an
 * output contract. There is no corrective reprompt for a subprompt (it has no
 * conversation to reprompt in), so the directive is the runtime's only lever
 * for getting parseable JSON back; a malformed or non-conforming reply is an
 * ordinary node failure handled by the caller, never retried in-band here.
 *
 * The object-only constraint is therefore stated twice, before and after the
 * schema: the serialized schema is a large blob, so a directive placed only
 * ahead of it is not the last thing the model reads.
 *
 * @docLink packages/factory-assets/flow#render-subprompt-prompt
 */
export function renderSubpromptPrompt(
  instruction: string,
  outputSchema: Record<string, unknown> | undefined,
): string {
  if (!outputSchema) return instruction;
  return `${instruction}\n\nRespond with a single JSON object matching this JSON Schema, and nothing else — no markdown code fences, no commentary before or after it:\n\n${JSON.stringify(outputSchema, null, 2)}\n\nReply with the object only. The first character of your reply must be \`{\` and the last must be \`}\` — no explanation, no restatement, no code fence.`;
}

/**
 * Single-line tag suitable for telemetry / logging. Derives a short, stable
 * summary of a stimulus that's safe to attach to spans, log entries, and
 * trace events. The kind is always included; node-specific stimuli also
 * include their `nodeId` so log queries can pivot on it.
 *
 * @docLink packages/factory-assets/flow#describe-stimulus
 */
export function describeStimulus(stimulus: TurnStimulus): string {
  switch (stimulus.kind) {
    case "flow_started":
      return "flow_started";
    case "approval_received":
      return `approval_received:${stimulus.nodeId}:${stimulus.decision}`;
    case "input_received":
      return `input_received:${stimulus.nodeId}`;
    case "retry_requested":
      return `retry_requested:${stimulus.nodeId}`;
    case "user_message":
      return `user_message:${stimulus.senderId}`;
    case "resumed_after_hibernation":
      return "resumed_after_hibernation";
    case "cancelled":
      return "cancelled";
    case "state_changed":
      return "state_changed";
  }
}
